// La salle d'attente — l'écran d'un compte qui n'est pas encore ouvert.
//
// ─────────────────────────────────────────────────────────────────────────────
// CE QUI A CHANGÉ, ET POURQUOI
//
// Jusqu'au 14 août 2026, un compte dont l'attestation n'était pas encore
// examinée entrait DANS la plateforme : il pouvait tout lire, et un bandeau lui
// expliquait qu'il ne pourrait publier qu'après validation. Ce n'était pas ce
// que l'église voulait — l'attestation de baptême est la porte, et une porte qui
// laisse entrer avant d'avoir regardé n'est pas une porte.
//
// Désormais, tant que l'équipe n'a pas validé, on ne voit pas la communauté :
// on voit cette page, et rien d'autre.
//
// ⚠️ LE TON. Koinonia n'est pas une église : c'est une plateforme, un réseau
// social. Ce n'est donc pas « un responsable de l'église » qui examine, c'est
// NOUS — l'équipe de Koinonia. La correction vient de Josaphat, le 14 août
// 2026, et elle vaut pour tout texte écrit ici : on ne parle jamais au nom
// d'une église, et on n'en invoque pas l'autorité.
//
// ⚠️ CE QUE CET ÉCRAN EST, ET CE QU'IL N'EST PAS.
// C'est une porte fermée dans l'APPLICATION. Ce n'est pas une barrière : les
// données restent protégées par les règles du serveur, qui, elles, n'ont pas
// changé — un compte en attente ne peut toujours rien écrire, et ne peut lire
// que son propre cercle, lequel est vide puisqu'il n'a encore ni ami ni
// abonnement. Autrement dit, ce que cette page cache est déjà vide. Elle
// remplace un écran désert par une explication, et c'est précisément ce qu'on
// lui demande.
//
// ─────────────────────────────────────────────────────────────────────────────
// LES DEUX PIÈGES ÉVITÉS
//
// · UN RESPONSABLE NE DOIT JAMAIS ÊTRE ENFERMÉ ICI. Si le compte d'un
//   modérateur portait un jour `en_attente`, il ne pourrait plus valider
//   personne — et plus personne ne pourrait entrer, lui compris. Les rôles
//   `moderateur` et `admin` traversent donc cette porte sans la voir.
// · L'E-MAIL NON CONFIRMÉ NE DOIT PAS DEVENIR UNE IMPASSE. Le bouton
//   « Renvoyer le lien » vivait dans le bandeau du haut, à l'intérieur de
//   l'application — devenu inatteignable. Il est donc reproposé ici.

import Store from "./store.js";
import { APP } from "./config.js";
import { esc, toast } from "./ui.js";

/** Les rôles qui ne sont jamais retenus à la porte. */
const PASSE_TOUJOURS = ["moderateur", "admin"];

/**
 * Ce membre doit-il rester à la porte ?
 *
 * @param {object} moi la fiche du membre connecté
 */
export function doitAttendre(moi) {
  if (!moi) return false;
  /* ⚠️ LE COMPTE SUPPRIMÉ PASSE AVANT LE RÔLE, et ce n'est pas un détail : un
     responsable supprimé doit rester dehors comme les autres, sinon la décision
     ne vaudrait rien contre celui qui en aurait le plus besoin. */
  if (moi.supprime) return true;
  if (PASSE_TOUJOURS.includes(moi.role)) return false;
  /* ⚠️ `email` REJOINT LA LISTE — 24 août 2026, et le jumeau natif aussi
     (`koinonia-native/ecrans/SalleAttente.jsx`). C'est l'état d'un compte
     inscrit dont l'adresse n'est pas encore confirmée.

     ⚠️ SANS LUI, LE NOUVEAU VENU ENTRAIT DANS LE FIL. Il y voyait les
     publications, les membres, les suggestions — avec un simple bandeau bleu
     l'invitant à confirmer son adresse. Josaphat l'a vu au premier essai :
     « à aucun moment l'utilisateur ne doit avoir accès au compte tant que le
     modérateur n'a pas cliqué sur accepter ». */
  return (
    moi.validation === "email" ||
    moi.validation === "en_attente" ||
    moi.validation === "refuse"
  );
}

const prenomDe = (moi) => esc(moi?.prenom || (moi?.nom || "").split(" ")[0] || "");

/* ⚠️ CE TEXTE PARLAIT ENCORE DE L'ATTESTATION DE BAPTÊME — corrigé le 23 août
   2026. Elle avait été retirée de l'inscription cinq jours plus tôt : on
   remerciait donc les nouveaux membres d'un document qu'on ne leur demandait
   plus, et qu'ils n'avaient jamais envoyé. Josaphat l'a vu sur l'écran d'un
   compte d'essai.

   ⚠️ ET IL DIT MAINTENANT LES DEUX ÉTAPES, DANS L'ORDRE. Confirmer son
   adresse est l'affaire du membre — c'est l'encart du code, juste en dessous.
   Ouvrir le compte est l'affaire du responsable. Un membre qui ne sait pas
   laquelle des deux le retient attend sans savoir quoi faire ; celui-ci sait
   qu'il lui reste un geste, puis une patience. */
/* ⚠️ ON NE PARLE DU RESPONSABLE QUE QUAND SON TOUR EST VENU — 24 août 2026.
   Tant que l'adresse n'est pas confirmée (`validation === "email"`), le dossier
   n'est même pas parti chez lui : annoncer un examen en cours décrirait une
   attente qui n'a pas commencé, et laisserait croire qu'il n'y a plus rien à
   faire. Le titre et le premier paragraphe changent donc avec l'état. */
function contenuEnAttente(moi) {
  return `
    <h1 class="attente__titre">${
      moi?.validation === "email"
        ? "Confirmez votre adresse pour continuer"
        : "Votre inscription est en cours d'examen"
    }</h1>
    <p class="attente__bonjour">Bonjour ${prenomDe(moi)},</p>
    <p>${
      moi?.validation === "email"
        ? `Votre inscription est enregistrée. Il reste une étape avant qu'un
           responsable puisse l'examiner : confirmer votre adresse e-mail avec
           le code ci-dessous.`
        : `Nous avons bien reçu votre inscription, et nous vous en remercions.
           Un responsable l'examine avant l'ouverture de votre compte.`
    }</p>
    <p>
      <strong>Ce délai n'excède habituellement pas ${APP.delaiValidationHeures} heures.</strong>
      Vous n'avez pas à vous réinscrire : votre demande est enregistrée.
    </p>
    <p>
      L'accès à Koinonia vous sera ouvert dès la fin de cette vérification.
      Elle garantit à chaque membre un espace fidèle à sa vocation.
    </p>
    <p class="attente__verset">
      « Que tout se fasse avec bienséance et avec ordre. »<br />
      <span class="petit texte-doux">1 Corinthiens 14:40</span>
    </p>
    <p class="attente__signature">
      Nous vous remercions de votre confiance.<br />
      <strong>L'équipe Koinonia</strong>
    </p>`;
}

/* Le compte supprimé. Sobre, court, sans reproche — et sans « Bonjour X » : la
   pierre tombale ne porte plus de prénom, et l'application n'a plus rien pour
   nommer cette personne. C'est justement la preuve que la suppression a eu
   lieu. */
function contenuSupprime() {
  return `
    <h1 class="attente__titre">Ce compte a été supprimé</h1>
    <p>
      Les données associées à ce compte ont été effacées de Koinonia et ne
      peuvent pas être rétablies.
    </p>
    <p>
      Si vous pensez qu'il s'agit d'une erreur, écrivez-nous : nous réexaminerons
      la décision.
    </p>
    <p class="attente__signature">
      <strong>L'équipe Koinonia</strong>
    </p>`;
}

function contenuRefuse(moi) {
  const motif = moi?.baptemeDecide?.motif;
  return `
    <h1 class="attente__titre">Votre inscription n'a pas pu être validée</h1>
    <p class="attente__bonjour">Bonjour ${prenomDe(moi)},</p>
    <p>
      Après examen, nous ne sommes pas en mesure de valider le document que vous
      nous avez transmis.
    </p>
    ${motif ? `<p class="attente__motif">${esc(motif)}</p>` : ""}
    <p>
      Vous pouvez nous adresser un nouveau document : votre dossier sera alors
      réexaminé dans les meilleurs délais. Votre compte reste conservé jusque-là.
    </p>
    <p class="attente__signature">
      Nous restons à votre disposition.<br />
      <strong>L'équipe Koinonia</strong>
    </p>`;
}

/* ══════════════ LA CONFIRMATION DE L'ADRESSE, PAR CODE ══════════════
   Portée du téléphone au site le 23 août 2026 — `koinonia-native/ecrans/
   SalleAttente.jsx` porte le jumeau, et les deux doivent dire la même chose.

   ⚠️ CE N'EST PLUS UN CONFORT, C'EST LA SEULE PORTE. Josaphat a retiré la
   validation manuelle le même jour : plus personne ne valide un compte à la
   main. Sans cet écran, quelqu'un qui s'inscrit depuis un navigateur
   confirmerait son adresse par le lien… et resterait en attente POUR
   TOUJOURS, sans personne pour l'ouvrir et sans moyen de se débloquer.

   ⚠️ L'ANCIEN « RENVOYER LE LIEN » A DISPARU, et il le fallait. Deux chemins
   pour la même chose — un lien ET un code — c'est un membre qui clique le lien,
   voit son adresse confirmée, et reste bloqué quand même parce que le lien ne
   pose pas `validation`. Seul le code ouvre la porte désormais.

   ⚠️ ET C'EST LE SERVEUR QUI DÉCIDE. Le code est tiré, envoyé et vérifié par le
   Worker `koinonia-pousser` ; c'est lui, avec le compte de service que le
   navigateur ne détient pas, qui pose `emailVerified`. Une page web se modifie
   depuis la console du navigateur : jamais un membre ne doit pouvoir s'y
   déclarer vérifié.

   ⚠️ IL DISPARAÎT UNE FOIS L'ADRESSE CONFIRMÉE — et il ne le faisait pas.
   L'ancien encart portait cette condition (`if (moi?.emailVerifie !== false)
   return ""`) ; je l'ai perdue en le remplaçant. Résultat : le membre tapait
   son code, la page se rechargeait, et « Recevoir mon code » réapparaissait
   comme si rien n'avait eu lieu. Josaphat l'a vu au premier essai, le 24 août
   2026 : « normalement je ne dois plus voir le bouton, je dois voir compte
   vérifié ».

   Un écran qui redemande ce qu'on vient de faire fait croire à un échec. */
function encartCode(moi) {
  /* ⚠️ C'EST LA FICHE QUI DÉCIDE, PAS LE JETON — 20 octobre 2026.
     Ce bloc se fiait à `emailVerifie` : adresse confirmée → encart vert, « il
     ne reste plus qu'à attendre ». Or seul le CODE fait entrer le dossier dans
     la file (`email` → `en_attente`, posé par le Worker) : un membre qui avait
     cliqué le LIEN du vieil e-mail était confirmé chez Google… et bloqué à
     vie — plus d'encart de code, et jamais dans la file du responsable.
     L'audit du 19 octobre l'a prouvé maillon par maillon. Tant que la fiche
     dit `email`, l'encart du code reste, quoi qu'en dise le jeton : c'est lui
     qui dépose le dossier. L'envoi du lien, lui, a été retiré de `signUp` —
     ceci rattrape ceux qu'il a déjà piégés. */
  if (moi?.validation !== "email")
    return `
      <div class="attente__encart attente__encart--fait">
        <p class="attente__fait">
          <span class="attente__coche" aria-hidden="true">✓</span>
          <strong>Adresse confirmée.</strong>
        </p>
        <p class="petit texte-doux">
          Il ne reste plus qu'à attendre l'approbation d'un responsable.
        </p>
      </div>`;

  return `
    <div class="attente__encart" data-code>
      <p>
        ${
          moi?.emailVerifie !== false
            ? `<strong>Une dernière étape.</strong>
               Votre adresse est confirmée, mais votre dossier n'est pas encore
               parti chez le responsable : recevez un code à six chiffres pour
               l'y déposer.`
            : `<strong>Confirmez votre adresse pour entrer.</strong>
               Nous envoyons un code à six chiffres à
               ${esc(moi?.email || "votre adresse")}.`
        }
      </p>
      <div data-code-repos>
        <button class="btn btn--primaire" data-demander>Recevoir mon code</button>
      </div>
      <div data-code-saisie hidden>
        <input class="champ champ--code" data-champ-code inputmode="numeric"
               maxlength="6" placeholder="000000" autocomplete="one-time-code" />
        <button class="btn btn--primaire" data-confirmer disabled>Confirmer</button>
        <button class="lien-nu" data-demander>Je n'ai rien reçu — renvoyer un code</button>
      </div>
      <p class="attente__mot petit" data-code-mot hidden></p>
    </div>`;
}

/**
 * Dessine la salle d'attente dans un conteneur.
 *
 * Rend une fonction de démontage, comme l'écran de connexion — c'est ce que
 * `app.js` attend pour nettoyer quand la session change.
 */
export function rendre(hote, moi) {
  const supprime = Boolean(moi?.supprime);
  // Un compte supprimé se présente comme un dossier clos : ni pastille qui
  // tourne, ni bouton pour réessayer, ni encart d'e-mail. Il ne reste qu'à
  // fermer la session — c'est la seule chose qui ait encore un sens.
  const refuse = supprime || moi?.validation === "refuse";
  /* Le FILIGRANE est un `<span>` vide au fond de la section : le logo en très
     grand, presque effacé, derrière tout le reste. Il donne à la page
     l'identité de la maison sans jamais gêner la lecture — c'est un fond, pas
     une illustration.

     La BARRE D'ÉTAT — trois points qui respirent — dit « on s'occupe de vous en
     ce moment » plutôt que « attendez ». Sur la page de refus elle n'a rien à
     faire : le dossier a été examiné, plus rien ne tourne. */
  const etat = refuse
    ? ""
    : `<span class="attente__etat">
         <span class="attente__pouls" aria-hidden="true"><i></i><i></i><i></i></span>
         Vérification en cours
       </span>`;

  hote.innerHTML = `
    <section class="attente${refuse ? " attente--refus" : ""}">
      <span class="attente__blason">
        <img class="attente__logo" src="icons/icon-192.png" alt="" width="72" height="72" />
      </span>
      <div class="attente__carte">
        <span class="attente__filigrane" aria-hidden="true"></span>
        ${etat}
        ${supprime ? contenuSupprime() : refuse ? contenuRefuse(moi) : contenuEnAttente(moi)}
        ${supprime || refuse ? "" : encartCode(moi)}
        <div class="attente__actions">
          ${refuse ? "" : `<button class="btn btn--primaire" data-verifier>Vérifier l'état de mon inscription</button>`}
          <button class="btn btn--fantome" data-quitter>Se déconnecter</button>
        </div>
      </div>
    </section>`;

  /* ── LE CODE ─────────────────────────────────────────────────────────────
     Trois éléments, et rien de plus : demander, saisir, confirmer. Le serveur
     fait tout le reste. */
  const bloc = hote.querySelector("[data-code]");
  if (bloc) {
    const repos = bloc.querySelector("[data-code-repos]");
    const saisie = bloc.querySelector("[data-code-saisie]");
    const champ = bloc.querySelector("[data-champ-code]");
    const confirmer = bloc.querySelector("[data-confirmer]");
    const mot = bloc.querySelector("[data-code-mot]");

    const dire = (texte, erreur = false) => {
      mot.textContent = texte;
      mot.hidden = !texte;
      mot.classList.toggle("attente__mot--erreur", erreur);
    };

    /* Les deux boutons « demander » — celui du repos et celui du renvoi —
       font exactement la même chose. */
    for (const b of bloc.querySelectorAll("[data-demander]"))
      b.onclick = async () => {
        for (const x of bloc.querySelectorAll("button")) x.disabled = true;
        dire("Envoi en cours…");
        try {
          const dit = await Store.auth.demanderCodeEmail();
          if (dit?.deja) {
            await Store.auth.rafraichirVerification();
            dire("Votre adresse était déjà confirmée.");
          } else {
            repos.hidden = true;
            saisie.hidden = false;
            dire("Code envoyé. Regardez votre boîte — et vos indésirables.");
            champ.focus();
          }
        } catch (e) {
          dire(e.message || "Envoi impossible.", true);
        }
        for (const x of bloc.querySelectorAll("button")) x.disabled = false;
        confirmer.disabled = champ.value.length !== 6;
      };

    /* ⚠️ ON NE GARDE QUE LES CHIFFRES. Un code recopié depuis un courriel
       traîne souvent une espace ou un retour à la ligne avec lui. */
    champ.oninput = () => {
      champ.value = champ.value.replace(/[^0-9]/g, "").slice(0, 6);
      confirmer.disabled = champ.value.length !== 6;
    };
    champ.onkeydown = (e) => {
      if (e.key === "Enter" && champ.value.length === 6) confirmer.click();
    };

    confirmer.onclick = async () => {
      for (const x of bloc.querySelectorAll("button")) x.disabled = true;
      dire("Vérification…");
      try {
        await Store.auth.verifierCodeEmail(champ.value);
        /* Le jeton vient d'être rafraîchi et la fiche est validée : on repart
           de zéro, comme pour « j'ai été validé » plus bas. Rafraîchir un
           écran à moitié serait plus risqué que de tout recharger. */
        dire("C'est confirmé. Bienvenue.");
        setTimeout(() => location.reload(), 900);
      } catch (e) {
        dire(e.message || "Code refusé.", true);
        for (const x of bloc.querySelectorAll("button")) x.disabled = false;
        confirmer.disabled = champ.value.length !== 6;
      }
    };
  }

  /* « J'ai été validé » recharge la page plutôt que de relire la fiche : la
     validation change ce que le membre a le droit de lire, et donc TOUTES les
     écoutes déjà posées. Repartir de zéro est plus sûr que de rafraîchir un
     écran à moitié. */
  const verifier = hote.querySelector("[data-verifier]");
  if (verifier) verifier.onclick = () => location.reload();

  /* ⚠️ `signOut`, PAS `logout` — 20 octobre 2026. `Store.auth.logout` n'a
     jamais existé : le bouton levait une TypeError muette, et un membre en
     attente — pour qui cette page est le SEUL écran — ne pouvait pas rendre
     la place sur un téléphone partagé. Trouvé à l'audit, deux fois. */
  hote.querySelector("[data-quitter]").onclick = () => Store.auth.signOut();

  return () => {
    hote.innerHTML = "";
  };
}
